/**
 * A single race, local or online.
 *
 * This owns everything that lives for the duration of one event: the
 * simulation (local) or the network mirror (online), the particles and tyre
 * marks, the camera, audio feedback and the results screen payload.
 */

import { clamp, damp, lerp } from '@shared/math.js';
import { buildTrack, type BuiltTrack } from '@shared/track.js';
import { SURFACES } from '@shared/surfaces.js';
import { RaceSim, raceRewards, type RaceConfig, type RaceEvent, type RaceResult, type RacerInit } from '@shared/race.js';
import { getCar, CAR_BY_ID } from '@shared/cars.js';
import { computeHandling } from '@shared/upgrades.js';
import { stepVehicle, type VehicleInput, type VehicleState, createVehicleState } from '@shared/vehicle.js';
import { nearestPathIndex } from '@shared/track.js';
import type { AIDifficulty } from '@shared/ai.js';
import type { RaceModeId } from '@shared/net.js';
import type { UpgradeLevels } from '@shared/upgrades.js';
import { bakeTrack, type BakedTrack } from '../render/track-art.js';
import { ParticleSystem, SkidLayer } from '../render/particles.js';
import { Camera } from '../core/camera.js';
import type { Renderer, CarDraw } from '../render/renderer.js';
import type { NetClient, SnapshotCar } from '../net/client.js';
import type { AudioEngine } from '../audio/audio.js';
import type { GameSettings } from '@shared/save.js';

/** The simulation always advances in these steps, regardless of frame rate. */
export const FIXED_STEP = 1 / 60;
/** Maximum simulation steps per frame, so a stalled tab cannot spiral. */
const MAX_CATCHUP_STEPS = 5;

export interface RaceSessionOptions {
  mode: RaceModeId;
  trackId: string;
  laps: number;
  aiCount: number;
  aiDifficulty: AIDifficulty;
  seed: number;
  /** The player's own racer definition. */
  player: { name: string; carId: string; color: string; upgrades: UpgradeLevels };
  displayName: string;
  net?: NetClient | null;
  settings: GameSettings;
  /** Elimination interval / checkpoint target for the specialised modes. */
  eliminationInterval?: number;
  checkpointTarget?: number;
  /** The player's existing best lap here, so the results screen can flag a PB. */
  previousBestLap?: number;
}

export interface HudState {
  speedKmh: number;
  gear: number;
  rpm: number;
  lap: number;
  totalLaps: number;
  /** Race format, so the HUD can show gates instead of laps where it matters. */
  mode: RaceModeId;
  checkpoint: number;
  checkpointTarget: number;
  position: number;
  racerCount: number;
  lapTime: number;
  bestLap: number;
  raceTime: number;
  driftScore: number;
  driftPending: number;
  driftCombo: number;
  countdown: number;
  wrongWay: boolean;
  turboCharge: number;
  surface: string;
  offTrack: boolean;
  finished: boolean;
  eliminated: boolean;
  standings: Array<{
    id: string;
    name: string;
    position: number;
    lap: number;
    isPlayer: boolean;
    finished: boolean;
    eliminated: boolean;
    gap: number;
    color: string;
  }>;
}

export interface RaceOutcomeData {
  results: RaceResult[];
  playerResult: RaceResult;
  rewards: ReturnType<typeof raceRewards>;
  mode: RaceModeId;
  trackId: string;
  difficulty: AIDifficulty;
  opponentCount: number;
}

export class RaceSession {
  readonly track: BuiltTrack;
  readonly baked: BakedTrack;
  readonly camera = new Camera();
  readonly particles = new ParticleSystem();
  readonly skids: SkidLayer;
  readonly mode: RaceModeId;
  readonly settings: GameSettings;
  readonly playerId: string;
  readonly isOnline: boolean;

  sim: RaceSim | null = null;
  private net: NetClient | null;
  private audio: AudioEngine;
  private options: RaceSessionOptions;

  /** Local prediction of the player's car in online mode. */
  private predicted: VehicleState | null = null;
  private lastSnapshotAt = 0;
  /** Fixed-timestep accumulator shared by local and predicted simulation. */
  private accumulator = 0;

  /** Countdown value shown on the HUD. */
  countdown = 0;
  /** Set once the player's own race is over. */
  playerFinished = false;
  playerFinishTime = 0;
  /** Accumulated results, or null while the race is running. */
  outcome: RaceOutcomeData | null = null;
  /** Set when the player crosses the line; used to hold the results screen. */
  finishedHoldTime = 0;

  /** Emitted events for the UI (lap banners, crash feedback, achievements). */
  readonly notifications: Array<{ text: string; kind: 'lap' | 'crash' | 'info' | 'achievement'; at: number }> = [];

  private previousPositions = new Map<string, number>();
  private skidAccumulator = 0;
  private audioFlags = { countedDown: -1, started: false };
  /** How many of `net.laps` have already produced a banner. */
  private lapsSeen = 0;
  /** The player's most recent completed lap, for the live lap timer. */
  private lastOnlineLap: { lapTime: number; serverTime: number } | null = null;

  constructor(options: RaceSessionOptions, audio: AudioEngine) {
    this.options = options;
    this.audio = audio;
    this.mode = options.mode;
    this.settings = options.settings;
    this.net = options.net ?? null;
    this.isOnline = this.mode === 'online' && this.net !== null;
    this.playerId = this.isOnline ? this.net!.playerId : 'player';
    this.track = buildTrack(options.trackId);
    this.baked = bakeTrack(this.track);
    this.skids = new SkidLayer(this.track.def.width, this.track.def.height);
    this.camera.userZoom = options.settings.cameraZoom;
    this.camera.shakeEnabled = options.settings.screenShake && !options.settings.reducedMotion;
    this.particles.quality = options.settings.reducedMotion ? 0.5 : 1;

    if (!this.isOnline) {
      this.sim = new RaceSim(this.localConfig(), this.localRacers());
      this.countdown = this.sim.countdown;
      this.camera.snapTo({
        x: this.track.path[0]!.x, y: this.track.path[0]!.y, speed: 0,
        angle: this.track.path[0]!.angle, vx: 0, vy: 0,
      });
    } else {
      this.countdown = 3.2;
      const start = this.track.path[0]!;
      this.predicted = createVehicleState(start.x, start.y, start.angle);
      this.camera.snapTo({ x: start.x, y: start.y, speed: 0, angle: start.angle, vx: 0, vy: 0 });
    }
  }

  private localConfig(): Partial<RaceConfig> {
    return {
      mode: this.mode,
      trackId: this.options.trackId,
      laps: this.options.laps,
      aiCount: this.options.aiCount,
      aiDifficulty: this.options.aiDifficulty,
      seed: this.options.seed,
      countdown: 3.2,
      eliminationInterval: this.options.eliminationInterval ?? 30,
      checkpointTarget: this.options.checkpointTarget ?? 6,
    };
  }

  private localRacers(): RacerInit[] {
    const inits: RacerInit[] = [
      {
        id: 'player',
        name: this.options.displayName,
        carId: this.options.player.carId,
        color: this.options.player.color,
        ai: false,
        isPlayer: true,
        upgrades: this.options.player.upgrades,
      },
    ];
    const others = ['zenith', 'dustback', 'vulcan', 'pico', 'kart', 'sideways', 'ridgeback', 'apogee'];
    const colors = ['#e8453c', '#31d7c8', '#f2c14e', '#8e6fd6', '#66c46a', '#f28c28', '#4fa3e8', '#e05d9c'];
    for (let i = 0; i < this.options.aiCount; i++) {
      const carId = others[i % others.length]!;
      const aiUpgrades: UpgradeLevels = {};
      // AI opponents are upgraded a little so the field feels varied.
      const level = Math.floor(i / 2);
      if (level > 0) {
        aiUpgrades.engine = Math.min(3, level);
        aiUpgrades.tires = Math.min(2, level);
      }
      inits.push({
        id: `ai${i}`,
        name: `${aiName(i)} · ${this.options.aiDifficulty}`,
        carId,
        color: colors[i % colors.length]!,
        ai: true,
        difficulty: this.options.aiDifficulty,
        upgrades: aiUpgrades,
      });
    }
    return inits;
  }

  // ---------------------------------------------------------------- update
  /**
   * Advances the race. `input` is the player's control this frame; `dt` is the
   * real frame delta (the simulation itself always runs on a fixed step).
   */
  update(dt: number, input: VehicleInput, actions: { reset: boolean }): void {
    if (this.isOnline) this.updateOnline(dt, input, actions);
    else this.updateLocal(dt, input, actions);
    this.emitEffects(dt);
    this.updateAudio(input);
    this.camera.update(this.cameraTarget(), dt, this.settings.reducedMotion);
    this.skids.update(dt);
    this.particles.update(dt);
    this.pruneNotifications();
  }

  private updateLocal(dt: number, input: VehicleInput, actions: { reset: boolean }): void {
    const sim = this.sim;
    if (!sim) return;
    this.countdown = Math.max(0, sim.countdown);

    if (actions.reset && sim.phase === 'racing') {
      // Recover the player when they are beached or have wandered off track.
      const player = sim.getRacer('player');
      if (player && (player.vehicle.stuckTime > 3 || !player.vehicle.onRoad)) {
        sim.respawn('player');
        this.notifications.push({ text: 'Recovered to the track', kind: 'info', at: Date.now() });
      }
    }

    sim.setInput('player', input);
    // Fixed timestep with a capped catch-up, so the physics is identical on a
    // 60 Hz laptop and a 165 Hz monitor but a stalled tab cannot spiral.
    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= FIXED_STEP && steps < MAX_CATCHUP_STEPS) {
      sim.step(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
      steps++;
      for (const event of sim.events) this.handleEvent(event);
    }
    if (steps === MAX_CATCHUP_STEPS) this.accumulator = 0;

    // The results screen is driven by *your* race ending, not by the last AI
    // crossing the line. Waiting for the whole field meant a player who had
    // already won sat watching a frozen car for a minute with nothing to do.
    const player = sim.getRacer('player');
    const playerDone = player !== undefined && (player.finished || player.eliminated);
    if (!this.outcome && (playerDone || sim.phase === 'finished')) this.buildOutcome();
  }

  private updateOnline(dt: number, input: VehicleInput, actions: { reset: boolean }): void {
    const net = this.net;
    const predicted = this.predicted;
    if (!net || !predicted) return;

    net.sendInput(input);
    // In an online race the server owns the car, so "recover my car" has to be a
    // request rather than a local teleport — a client-side version would be undone
    // by the very next reconciliation. Only ask while actually racing.
    if (actions.reset && !this.playerFinished && this.countdown <= 0) net.requestReset();
    this.countdown = Math.max(0, this.countdown - dt);
    net.interpolate(dt);
    this.consumeOnlineLaps();

    const snapshot = net.localSnapshot();
    const carId = this.options.player.carId;
    const handling = computeHandling(getCar(carId), this.options.player.upgrades);

    if (this.countdown <= 0) {
      this.accumulator += dt;
      let steps = 0;
      while (this.accumulator >= FIXED_STEP && steps < MAX_CATCHUP_STEPS) {
        stepVehicle(predicted, handling, input, this.track, FIXED_STEP);
        this.accumulator -= FIXED_STEP;
        steps++;
      }
      if (steps === MAX_CATCHUP_STEPS) this.accumulator = 0;
      // Gentle reconciliation toward the authoritative position.
      if (snapshot) {
        const error = Math.hypot(snapshot.x - predicted.x, snapshot.y - predicted.y);
        if (error > 90) {
          predicted.x = snapshot.x;
          predicted.y = snapshot.y;
          predicted.angle = snapshot.angle;
          predicted.vx = snapshot.vx;
          predicted.vy = snapshot.vy;
        } else if (error > 2) {
          const k = 0.18;
          predicted.x = lerp(predicted.x, snapshot.x, k);
          predicted.y = lerp(predicted.y, snapshot.y, k);
          predicted.angle = predicted.angle + shortestDelta(predicted.angle, snapshot.angle) * k;
          predicted.vx = lerp(predicted.vx, snapshot.vx, k);
          predicted.vy = lerp(predicted.vy, snapshot.vy, k);
        }
        predicted.speed = snapshot.speed;
        predicted.gear = snapshot.gear;
        predicted.rpm = snapshot.rpm;
        predicted.slip = snapshot.slip;
        predicted.impact = snapshot.impact;
        if (snapshot.impact > 60) this.camera.shake(snapshot.impact);
      }
    }
    this.lastSnapshotAt = Date.now();

    if (net.results && !this.outcome) this.buildOnlineOutcome(net.results);
  }

  private cameraTarget(): { x: number; y: number; speed: number; angle: number; vx: number; vy: number } {
    const state = this.playerVehicle();
    if (!state) {
      const start = this.track.path[0]!;
      return { x: start.x, y: start.y, speed: 0, angle: start.angle, vx: 0, vy: 0 };
    }
    return { x: state.x, y: state.y, speed: state.speed, angle: state.angle, vx: state.vx, vy: state.vy };
  }

  playerVehicle(): VehicleState | null {
    if (this.isOnline) return this.predicted;
    return this.sim?.getRacer('player')?.vehicle ?? null;
  }

  private handleEvent(event: RaceEvent): void {
    switch (event.type) {
      case 'countdown':
        if (event.value >= 0 && this.audioFlags.countedDown !== event.value) {
          this.audioFlags.countedDown = event.value;
          this.audio.sfx(event.value === 0 ? 'go' : 'countdown');
        }
        break;
      case 'start':
        this.audio.sfx('go');
        break;
      case 'lap': {
        const player = this.sim?.getRacer('player');
        if (player && event.racerId === player.id) {
          this.audio.sfx('lap');
          this.notifications.push({
            text: event.best ? `Lap ${event.lap} · ${formatLap(event.lapTime)} — personal best!` : `Lap ${event.lap} · ${formatLap(event.lapTime)}`,
            kind: 'lap',
            at: Date.now(),
          });
        }
        break;
      }
      case 'finish': {
        const player = this.sim?.getRacer('player');
        if (player && event.racerId === player.id) {
          this.playerFinished = true;
          this.playerFinishTime = event.time;
          this.audio.sfx('finish');
        }
        break;
      }
      case 'crash': {
        const player = this.sim?.getRacer('player');
        if (player && event.racerId === player.id && event.impact > 60) {
          this.camera.shake(event.impact);
          this.audio.sfx('crash', clamp(event.impact / 180, 0.2, 1.4));
          this.particles.sparks(event.x, event.y, -Math.cos(player.vehicle.angle), -Math.sin(player.vehicle.angle), event.impact);
        }
        break;
      }
      case 'eliminated': {
        const player = this.sim?.getRacer('player');
        if (player && event.racerId === player.id) {
          this.notifications.push({ text: 'Eliminated', kind: 'crash', at: Date.now() });
        }
        break;
      }
      default:
        break;
    }
  }

  /** Particles, tyre marks and impact feedback for every car. */
  private emitEffects(dt: number): void {
    const vehicles = this.allVehicles();
    this.skidAccumulator += dt;
    const drawSkids = this.skidAccumulator >= 1 / 30;
    if (drawSkids) this.skidAccumulator = 0;

    for (const entry of vehicles) {
      const v = entry.state;
      const handling = entry.handling;
      if (v.speed < 12) continue;
      const surface = SURFACES[v.surfaceRear];
      const cs = Math.cos(v.angle);
      const sn = Math.sin(v.angle);
      const halfLong = handling.wheelBase / 2;
      const halfLat = handling.width * 0.42;
      const slip = v.slip;
      const spin = v.wheelSpin;
      const marks = Math.min(1, slip * 1.3 + spin * 0.9);

      for (const side of [-1, 1]) {
        const wx = v.x - cs * halfLong - sn * halfLat * side;
        const wy = v.y - sn * halfLong + cs * halfLat * side;
        if (surface.marks && drawSkids && marks > 0.18) {
          const length = v.speed * (1 / 30);
          this.skids.mark(wx, wy, wx - cs * length, wy - sn * length, 3.4, marks * 0.5 * surface.skid, '#141518');
        }
        if (surface.particle > 0.05 && marks > 0.22 && Math.random() < marks * surface.particle) {
          this.particles.tyreSmoke(wx, wy, -cs * 20, -sn * 20, marks * surface.particle, surface.particleColor, 1);
        }
        if (spin > 0.45 && surface.particle > 0.1 && Math.random() < 0.4) {
          this.particles.tyreSmoke(wx, wy, -cs * 40, -sn * 40, spin, '#cfd6de', 1);
        }
      }

      if (v.impact > 70) {
        this.particles.sparks(v.x, v.y, v.impactNx, v.impactNy, v.impact);
        this.particles.debris(v.x, v.y, v.vx, v.vy, surface.particleColor, 4);
      }
      if (entry.isPlayer && v.impact > 70) {
        this.audio.sfx('crash', clamp(v.impact / 200, 0.2, 1.3));
      }
    }
  }

  private allVehicles(): Array<{ state: VehicleState; handling: ReturnType<typeof computeHandling>; isPlayer: boolean }> {
    if (this.sim) {
      return this.sim.racers.map((racer) => ({
        state: racer.vehicle,
        handling: racer.handling,
        isPlayer: racer.isPlayer,
      }));
    }
    const predicted = this.predicted;
    if (!predicted) return [];
    return [
      {
        state: predicted,
        handling: computeHandling(getCar(this.options.player.carId), this.options.player.upgrades),
        isPlayer: true,
      },
    ];
  }

  private updateAudio(input: VehicleInput): void {
    const state = this.playerVehicle();
    if (!state) return;
    this.audio.updateEngine(
      {
        rpm: state.rpm,
        throttle: input.throttle,
        speed: state.speed,
        gear: state.gear,
        boosting: input.boost && state.turboCharge < 0.99,
        slip: state.slip,
        wheelSpin: state.wheelSpin,
        surface: SURFACES[state.surfaceRear].sound,
        onRoad: state.onRoad,
        active: this.countdown <= 0 && !this.playerFinished,
      },
      1 / 60,
    );
  }

  private pruneNotifications(): void {
    const now = Date.now();
    while (this.notifications.length > 0 && now - this.notifications[0]!.at > 3200) this.notifications.shift();
  }

  // --------------------------------------------------------------- results
  private buildOutcome(): void {
    const sim = this.sim;
    if (!sim) return;
    const results = sim.results();
    const playerResult = results.find((r) => r.id === 'player') ?? results[results.length - 1]!;
    // Purely for the immediate results screen: the server recomputes the
    // authoritative payout from the submitted result.
    const rewards = raceRewards(
      playerResult,
      sim.config,
      Math.max(0, sim.racers.length - 1),
      this.options.aiDifficulty,
      this.options.previousBestLap ?? 0,
    );
    this.playerFinished = true;
    this.playerFinishTime = playerResult.time;
    this.outcome = {
      results,
      playerResult,
      rewards,
      mode: this.mode,
      trackId: this.options.trackId,
      difficulty: this.options.aiDifficulty,
      opponentCount: Math.max(0, sim.racers.length - 1),
    };
  }

  private buildOnlineOutcome(rows: NonNullable<NetClient['results']>): void {
    const results: RaceResult[] = rows.map((row) => ({
      id: row.id,
      name: row.name,
      carId: row.carId,
      color: '#fff',
      ai: false,
      position: row.position,
      laps: row.laps,
      time: row.time,
      bestLap: row.bestLap,
      lapTimes: [],
      finished: row.finished,
      eliminated: false,
      driftScore: row.driftScore,
      respawns: 0,
      topSpeed: 0,
      offTrackTime: 0,
    }));
    const playerResult = results.find((r) => r.id === this.playerId);
    if (!playerResult) return;
    this.playerFinished = true;
    this.outcome = {
      results,
      playerResult,
      rewards: raceRewards(playerResult, { ...this.localConfig(), mode: 'online' } as RaceConfig, Math.max(0, results.length - 1), this.options.aiDifficulty, 0),
      mode: 'online',
      trackId: this.options.trackId,
      difficulty: this.options.aiDifficulty,
      opponentCount: Math.max(0, results.length - 1),
    };
  }

  // ------------------------------------------------------------------ HUD
  hud(): HudState {
    if (this.isOnline) return this.netHud();
    const sim = this.sim!;
    const player = sim.getRacer('player')!;
    const vehicle = player.vehicle;
    const standings = sim.racers
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((racer) => ({
        id: racer.id,
        name: racer.name,
        position: racer.position,
        lap: racer.lapsCompleted,
        isPlayer: racer.isPlayer,
        finished: racer.finished,
        eliminated: racer.eliminated,
        gap: player.progress - racer.progress,
        color: racer.color,
      }));
    return {
      speedKmh: vehicle.speed * 0.24,
      gear: vehicle.gear,
      rpm: vehicle.rpm,
      lap: Math.max(1, Math.min(player.lap, sim.config.laps)),
      totalLaps: sim.config.laps,
      mode: sim.config.mode,
      checkpoint: player.checkpointsPassed,
      checkpointTarget: sim.config.checkpointTarget,
      position: player.position,
      racerCount: sim.racers.length,
      lapTime: player.armed && !player.finished ? sim.time - player.lapStartTime : player.lastLapTime,
      bestLap: Number.isFinite(player.bestLap) ? player.bestLap : 0,
      raceTime: sim.time,
      driftScore: Math.round(player.driftScore + player.pendingDrift),
      driftPending: player.pendingDrift,
      driftCombo: player.driftCombo,
      countdown: sim.countdown,
      wrongWay: player.wrongWay,
      turboCharge: vehicle.turboCharge,
      surface: SURFACES[vehicle.surfaceRear].name,
      offTrack: !vehicle.onRoad,
      finished: player.finished,
      eliminated: player.eliminated,
      standings,
    };
  }

  private netHud(): HudState {
    const net = this.net!;
    const snapshot = net.localSnapshot();
    const cars = net.lastSnapshotCars;
    const standings = cars
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((car) => ({
        id: car.id,
        name: car.id === net.playerId ? this.options.displayName : this.remoteName(car.id),
        position: car.position,
        lap: car.lap,
        isPlayer: car.id === net.playerId,
        finished: car.finished,
        eliminated: car.eliminated,
        gap: (snapshot?.lap ?? 0) - car.lap,
        color: this.remoteColor(car.id),
      }));
    const vehicle = this.predicted;
    return {
      speedKmh: (snapshot?.speed ?? vehicle?.speed ?? 0) * 0.24,
      gear: snapshot?.gear ?? vehicle?.gear ?? 1,
      rpm: snapshot?.rpm ?? vehicle?.rpm ?? 0,
      lap: Math.max(1, (snapshot?.lap ?? 0) + 1),
      totalLaps: this.options.laps,
      mode: 'online',
      checkpoint: 0,
      checkpointTarget: 0,
      position: snapshot?.position ?? 1,
      racerCount: Math.max(1, cars.length),
      lapTime: this.onlineLapTime(),
      bestLap: this.onlineBestLap(),
      raceTime: net.raceTime,
      driftScore: 0,
      driftPending: 0,
      driftCombo: 1,
      countdown: this.countdown,
      wrongWay: false,
      turboCharge: vehicle?.turboCharge ?? 1,
      surface: SURFACES[(snapshot?.surfaceRear ?? 'asphalt') as keyof typeof SURFACES]?.name ?? 'Track',
      offTrack: false,
      finished: snapshot?.finished ?? false,
      eliminated: snapshot?.eliminated ?? false,
      standings,
    };
  }

  private remoteMeta = new Map<string, { name: string; color: string; sprite?: string }>();

  /** Records the display details of an online opponent for names and sprites. */
  registerRemoteMeta(id: string, name: string, color: string, sprite?: string): void {
    this.remoteMeta.set(id, { name, color, sprite });
  }

  private remoteName(id: string): string {
    return this.remoteMeta.get(id)?.name ?? id;
  }

  /**
   * Lap time for the HUD. The server sends a lap message each time anyone
   * completes a lap, so the current lap is measured from the player's own last
   * crossing on the *server's* clock — mixing in a wall clock here would drift.
   */
  private onlineLapTime(): number {
    const net = this.net;
    if (!net) return 0;
    const mine = this.lastOnlineLap;
    return mine ? Math.max(0, net.raceTime - mine.serverTime) : net.raceTime;
  }

  private onlineBestLap(): number {
    const net = this.net;
    if (!net) return 0;
    let best = 0;
    for (const lap of net.laps) {
      if (lap.playerId !== net.playerId) continue;
      if (!lap.valid || lap.lapTime <= 0) continue;
      if (best === 0 || lap.lapTime < best) best = lap.lapTime;
    }
    return best;
  }

  /** Drains new lap messages so lap banners and audio play online too. */
  private consumeOnlineLaps(): void {
    const net = this.net;
    if (!net) return;
    for (let i = this.lapsSeen; i < net.laps.length; i++) {
      const message = net.laps[i]!;
      const isMe = message.playerId === net.playerId;
      if (message.lapTime <= 0) continue;
      // The server clock at the moment of crossing, used by the live lap timer.
      if (isMe) this.lastOnlineLap = { lapTime: message.lapTime, serverTime: net.raceTime };
      if (!isMe) {
        // Opponents still get a banner so you can see the race unfolding.
        this.notifications.push({
          text: `${this.remoteName(message.playerId)} · ${formatLap(message.lapTime)}`,
          kind: 'lap',
          at: Date.now(),
        });
        continue;
      }
      if (!message.valid) {
        this.notifications.push({ text: `Lap ${message.lap} not counted — you cut the course.`, kind: 'info', at: Date.now() });
        continue;
      }
      this.audio.sfx('lap');
      const isBest = message.bestLap > 0 && message.lapTime <= message.bestLap + 1e-6;
      this.notifications.push({
        text: isBest
          ? `Lap ${message.lap} · ${formatLap(message.lapTime)} — personal best!`
          : `Lap ${message.lap} · ${formatLap(message.lapTime)}`,
        kind: 'lap',
        at: Date.now(),
      });
    }
    this.lapsSeen = net.laps.length;
  }

  private remoteColor(id: string): string {
    return this.remoteMeta.get(id)?.color ?? '#8899aa';
  }

  /** Builds the car draw list for the renderer. */
  carDraws(): CarDraw[] {
    const out: CarDraw[] = [];
    if (this.sim) {
      for (const racer of this.sim.racers) {
        const car = CAR_BY_ID[racer.carId] ?? getCar(racer.carId);
        out.push({
          x: racer.vehicle.x,
          y: racer.vehicle.y,
          angle: racer.vehicle.angle,
          length: racer.handling.length,
          width: racer.handling.width,
          sprite: car.sprite,
          color: racer.color,
          isPlayer: racer.isPlayer,
          boosting: racer.vehicle.turboCharge < 0.9 && racer.vehicle.speed > 80,
          eliminated: racer.eliminated,
          finishing: racer.finished,
        });
      }
      return out;
    }
    const net = this.net!;
    const predicted = this.predicted;
    if (predicted) {
      const car = getCar(this.options.player.carId);
      out.push({
        x: predicted.x, y: predicted.y, angle: predicted.angle,
        length: car.handling.length, width: car.handling.width,
        sprite: car.sprite, color: this.options.player.color, isPlayer: true,
      });
    }
    for (const remote of net.remote.values()) {
      const meta = this.remoteMeta.get(remote.id);
      const car = meta?.sprite ? CAR_BY_ID[meta.sprite] : undefined;
      out.push({
        x: remote.x,
        y: remote.y,
        angle: remote.angle,
        length: car?.handling.length ?? 38,
        width: car?.handling.width ?? 21,
        sprite: car?.sprite ?? 'tuner',
        color: meta?.color ?? '#8899aa',
        isPlayer: false,
        boosting: remote.boost,
      });
    }
    return out;
  }

  /** Cars that are not the player, for the minimap. */
  minimapDots(): Array<{ x: number; y: number; isPlayer: boolean; color: string }> {
    if (this.sim) {
      return this.sim.racers.map((r) => ({ x: r.vehicle.x, y: r.vehicle.y, isPlayer: r.isPlayer, color: r.color }));
    }
    const dots: Array<{ x: number; y: number; isPlayer: boolean; color: string }> = [];
    if (this.predicted) dots.push({ x: this.predicted.x, y: this.predicted.y, isPlayer: true, color: this.options.player.color });
    for (const remote of this.net!.remote.values()) {
      dots.push({ x: remote.x, y: remote.y, isPlayer: false, color: this.remoteMeta.get(remote.id)?.color ?? '#8899aa' });
    }
    return dots;
  }

  /** Which checkpoint the player should aim for (practice assist). */
  nextCheckpoint(): { x: number; y: number } | null {
    if (!this.sim) return null;
    const player = this.sim.getRacer('player');
    if (!player) return null;
    const gate = this.track.checkpoints[player.checkpoint % this.track.checkpoints.length];
    return gate ? { x: gate.x, y: gate.y } : null;
  }

  /** Releases the skid layer and lets the GC have the baked art. */
  dispose(): void {
    this.skids.clear();
    this.particles.clear();
  }

  /** Progress 0..1 for a loading bar between races. */
  get leaderProgress(): number {
    if (!this.sim) return 0;
    let best = 0;
    for (const racer of this.sim.racers) best = Math.max(best, racer.progress);
    return clamp(best / Math.max(1, this.track.totalLength * this.options.laps), 0, 1);
  }

  /** Where the player currently sits along the lap, for the minimap arrow. */
  playerPathIndex(): number {
    const state = this.playerVehicle();
    if (!state) return 0;
    return nearestPathIndex(this.track, state.x, state.y);
  }

  /** Distance remaining for the HUD's interval readout. */
  gapToLeader(): number {
    if (!this.sim) return 0;
    const player = this.sim.getRacer('player');
    if (!player) return 0;
    let best = player.progress;
    for (const racer of this.sim.racers) best = Math.max(best, racer.progress);
    return best - player.progress;
  }

  /** Reference for the HUD's smooth speed readout. */
  speedSmoothing = 0;

  smoothSpeed(target: number, dt: number): number {
    this.speedSmoothing = lerp(this.speedSmoothing, target, damp(9, dt));
    return this.speedSmoothing;
  }
}

function shortestDelta(from: number, to: number): number {
  let d = to - from;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function aiName(index: number): string {
  const names = ['Rex', 'Nova', 'Kaito', 'Mira', 'Bolt', 'Sable', 'Ivo', 'Juno', 'Wren'];
  return names[index % names.length]!;
}

function formatLap(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '--:--.---';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

export type { SnapshotCar };
